import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import useLuxPlusRenewalBonus, { getCurrentRenewalRewardStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import { RenewalTextSkeleton } from '../LandingPage/LuxPlusRenewalBonusTimeline'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useCallback, type ReactNode } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

interface Props {
  item: App.MemberSubscriptionItem
}

const messages = defineMessages({
  title: {
    id: 'HB6UJjaADS4BM6nkchmX',
    defaultMessage: 'Your renewal has been cancelled',
    description: 'Title confirming the members renewal has been cancelled',
  },
  thankYou: {
    id: 'p3Dt68CC5U7cODYCYLB5',
    defaultMessage: 'Thank you for being a part of {programName}.',
    description: 'Thank-you line on the renewal cancelled confirmation screen',
  },
  bodyWithBonus: {
    id: '8mj8hlZR5gZCooA4AUzP',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind, renew your membership and receive your bonus {loyaltyPoints} and {statusCredits}.',
    description: 'Reminder that the member can still renew to receive their bonus points and status credits',
  },
  bodyWithPoints: {
    id: 'S_iXb5K_TKwoNyayRQft',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind, renew your membership and receive your bonus {loyaltyPoints}.',
    description: 'Reminder that the member can still renew to receive their bonus points',
  },
  bodyNoBonus: {
    id: 'qzwGSBf0BG7v8nymvqpb',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind and renew your membership.',
    description: 'Reminder that the member can still change their mind and renew before the membership year ends',
  },
  close: {
    id: 'sO7IRSznzFiwdtTnKZ4x',
    defaultMessage: 'Close',
    description: 'Button to dismiss the renewal cancelled confirmation screen',
  },
})

/**
 * Redesigned renewal-cancellation confirmation ("Your renewal has been cancelled"), shown when the
 * LuxPlus+ tiered renewal reward experience is enabled. The pre-flag design lives in
 * {@link LuxPlusManageAccountModalRenewalCancellationSuccess}.
 */
function LuxPlusManageAccountModalRenewalCancellationSuccessTiered({ item }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const { steps, isLoading } = useLuxPlusRenewalBonus()

  const nextReward = getCurrentRenewalRewardStep(steps)
  const points = nextReward?.points
  const statusCredits = nextReward?.statusCredits

  const showBonus = isSocieteMember && points !== undefined
  const showStatusCredits = showBonus && !hideStatusCredits && statusCredits !== undefined
  // Wait for the reward amounts before committing to a copy variant, so a Société member doesn't
  // see the no-bonus sentence flash and reflow into the bonus sentence once the fetch resolves.
  const isAwaitingBonusAmounts = isSocieteMember && isLoading

  const date = <FormatDateTime value={item.expiryDate} format="dmy-casual" />
  const b = (chunks: Array<ReactNode>) => <b>{chunks}</b>

  return <>
    <ModalHeader title={intl.formatMessage(messages.title)} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage(messages.thankYou, { programName: LUXURY_PLUS.PROGRAM_NAME })}
          </BodyText>
          {isAwaitingBonusAmounts
            ? <RenewalTextSkeleton lineWidths={[320, 300, 160]} height={18} />
            : <BodyText variant="medium" colour="light-foreground-alt">
                {showStatusCredits && <FormattedMessage
                  {...messages.bodyWithBonus}
                  values={{
                    date,
                    b,
                    loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                    statusCredits: <FormatLoyaltyStatusCredits statusCredits={statusCredits} />,
                  }}
                />}
                {!showStatusCredits && showBonus && <FormattedMessage
                  {...messages.bodyWithPoints}
                  values={{ date, b, loyaltyPoints: <FormatLoyaltyPoints points={points} /> }}
                />}
                {!showStatusCredits && !showBonus && <FormattedMessage {...messages.bodyNoBonus} values={{ date, b }} />}
            </BodyText>}
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      secondaryActionProps={{ children: intl.formatMessage(messages.close), onClick: dismissModal }}
    />
  </>
}

export default LuxPlusManageAccountModalRenewalCancellationSuccessTiered
